import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import R from 'ramda';
import classNames from 'classnames';
import { connect } from 'react-redux';
import Heading from '@cotyorg/ccx-atoms/Heading';
import IconButton from '@cotyorg/ccx-molecules/IconButton';
import Icon from '@cotyorg/ccx-atoms/Icon';
import ShowHide from '@cotyorg/ccx-utils/js/components/ShowHide';
import { CardsCarousel } from '@cotyorg/ccx-organism-carousel';
import Breadcrumbs from '@cotyorg/ccx-molecules/Breadcrumbs';
import { ProductGalleryThumbnails } from '@cotyorg/ccx-organism-product-gallery';
import { trackCarouselClickEvent } from '@cotyorg/ccx-utils/js/tracking/adobe-dtm/events/carousel';
import OptionalSection from '@cotyorg/ccx-utils/js/components/OptionalSection';
import StandardLayout from '../../templates/StandardLayout';
import ProductInfo from '../../organisms/ProductInfo';
import NumberOfRetailers from '../../organisms/NumberOfRetailers';
import { isSmallBreakpoint } from '../../utils';
import { getBasketProduct } from './modules/selectors';
import { toggle as toggleAction } from './modules/actions';
import { schema } from './helpers';
import { fetchRetailers as fetchRetailersAction } from '../../organisms/ProductRetailers/modules';
import ShadeFinder from '../../organisms/ShadeFinder/components/ShadeFinder';

const cardsPerPageMap = {
  xsmall: 1,
  small: 2,
  medium: 3,
  large: 4,
  xlarge: 4,
};

const card = {
  cardsStoreDataKey: 'relatedProducts',
  isTitleUnderlined: false,
  hasBackground: false,
  isSmallTitle: true,
  fullWidth: false,
  headingLevel: 5,
  fixedHeight: true,
  zoomOnHover: true,
  hideDescription: true,
};

const carousel = {
  carouselsStoreDataKey: 'relatedProductsCarousel',
  headingLevel: 5,
  rightChevronIconSrc: '/ccx-files/assets/arrow-right.svg',
  leftChevronIconSrc: '/ccx-files/assets/arrow-left.svg',
  onIndexChange: ({ type, index, isForward }) =>
    trackCarouselClickEvent({
      page: 'product',
      carousel: 'relatedProductsCarousel',
      type,
      index,
      isForward,
    }),
};

const generateContainerClassNames = (isSmall) =>
  classNames('grid-container', {
    full: isSmall,
  });

const Product = ({
  productDetails,
  sectionHeadings,
  currentBreakpoint,
  swatches,
  selectedSwatch,
  retailerInfo,
  basketProduct,
  breadcrumb,
  isIngredientsOpen = false,
  toggle,
  subcategoryTitle,
  categoryTitle,
  tracking,
  retailers,
  presentationLocale,
  bvConfig,
  customProductBuyLink,
  currentUrl,
  hideNumberOfRetailers,
  hidePrice,
  fetchRetailers,
  skus,
  shadeFinder,
  enableShadeFinder,
  productSlug,
}) => {
  useEffect(() => {
    fetchRetailers({ skus, locale: presentationLocale });
  }, []);

  const isShadeFinderEnabled = Boolean(enableShadeFinder && shadeFinder);

  return (
    <StandardLayout>
      <div className="product-page">
        <Breadcrumbs items={breadcrumb} />
        <div className="pre-offset">
          <div
            className={generateContainerClassNames(
              isSmallBreakpoint({ currentBreakpoint })
            )}
          >
            <div className="product-page__product">
              <div className="grid-x">
                {isSmallBreakpoint({ currentBreakpoint }) && (
                  <div className="cell xsmall-16">
                    <div className="product-page__heading">
                      <Heading extraClass="h2" level={1}>
                        {R.path(['productDetails', 'heading'], sectionHeadings)}
                      </Heading>

                      <OptionalSection
                        renderCondition={!!R.prop('isLocaleWithBv', bvConfig)}
                        section={() => (
                          <div
                            data-bv-show="rating_summary"
                            data-bv-product-id={R.prop('bvid', productDetails)}
                          />
                        )}
                      />

                      <NumberOfRetailers />
                    </div>
                  </div>
                )}

                <div className="cell xsmall-16 medium-8 large-8 xlarge-9">
                  <div className="product-page__product-gallery">
                    {isShadeFinderEnabled && (
                      <Icon
                        extraClass="product-page__product-gallery__lips"
                        iconSrc="/ccx-files/assets/lips.png"
                      />
                    )}
                    <ProductGalleryThumbnails
                      openBeforeAfterSliderInModal={R.includes(
                        'small',
                        currentBreakpoint
                      )}
                      headingLevel={3}
                      rightChevronIconSrc="/ccx-files/assets/arrow-right.svg"
                      leftChevronIconSrc="/ccx-files/assets/arrow-left.svg"
                      carouselsStoreDataKey="productGallery"
                      onIndexChange={({ type, index, isForward }) =>
                        trackCarouselClickEvent({
                          page: 'product',
                          carousel: 'productGallery',
                          type,
                          index,
                          isForward,
                        })
                      }
                    />
                  </div>

                  {isShadeFinderEnabled && (
                    <div className="product-page__section-try-on">
                      <ShadeFinder
                        shadeFinderConfiguration={shadeFinder}
                        vtoActualProductSlug={productSlug}
                        actualProductSku={R.prop('sku', selectedSwatch)}
                      />
                    </div>
                  )}
                </div>

                <div className="cell xsmall-16 medium-8 large-8 xlarge-7">
                  <ProductInfo
                    sectionHeadings={sectionHeadings}
                    productDetails={productDetails}
                    currentBreakpoint={currentBreakpoint}
                    swatches={swatches}
                    selectedSwatch={selectedSwatch}
                    retailerInfo={retailerInfo}
                    basketProduct={basketProduct}
                    subcategoryTitle={subcategoryTitle}
                    categoryTitle={categoryTitle}
                    presentationLocale={presentationLocale}
                    showStarRating={!!R.prop('isLocaleWithBv', bvConfig)}
                    customProductBuyLink={customProductBuyLink}
                    hideNumberOfRetailers={hideNumberOfRetailers}
                    hidePrice={hidePrice}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="show-ingredients">
          <div className="grid-container">
            <div className="grid-x grid-padding-x">
              <div className="cell xsmall-16 medium-16 xlarge-10 xlarge-offset-3">
                <div className="grid-x align-center">
                  {isIngredientsOpen ? (
                    <IconButton
                      testAttribute="hide-ingredients-button"
                      iconSrc="/ccx-files/assets/minus-within-circle.svg"
                      size="medium"
                      iconWidth={48}
                      extraClass="show-ingredients__show-more-button"
                      onClick={toggle}
                    >
                      {R.prop('hideIngredients', productDetails)}
                    </IconButton>
                  ) : (
                    <IconButton
                      testAttribute="show-ingredients-button"
                      iconSrc="/ccx-files/assets/plus-within-circle.svg"
                      size="medium"
                      iconWidth={48}
                      extraClass="show-ingredients__show-more-button"
                      onClick={toggle}
                    >
                      {R.prop('showIngredients', productDetails)}
                    </IconButton>
                  )}
                </div>

                <ShowHide
                  isOpen={isIngredientsOpen}
                  extraClass="show-ingredients__show-hide"
                  noTransitionClass="show-ingredients__show-hide--transition"
                >
                  <div
                    className="show-ingredients__show-hide__text text--center"
                    data-test="product-ingredients"
                  >
                    {R.prop('ingredients', selectedSwatch)}
                  </div>
                </ShowHide>
              </div>
            </div>
          </div>
        </div>

        <div className="product-reviews" id="reviews">
          <div className="grid-container">
            <div className="grid-x grid-padding-x">
              <div className="cell xsmall-16">
                <Heading level={2} data-test="product-reviews-heading">
                  {R.path(['reviews', 'heading'], sectionHeadings)}
                </Heading>

                <div
                  data-bv-show="reviews"
                  data-bv-productid={R.prop('bvid', productDetails)}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="related-products">
          <div className="grid-container">
            <div className="grid-x grid-padding-x">
              <div className="cell xsmall-16">
                <Heading level={2} extraClass="related-products__heading">
                  {R.path(['relatedProducts', 'heading'], sectionHeadings)}
                </Heading>

                <CardsCarousel
                  cardsPerPageMap={cardsPerPageMap}
                  cards={card}
                  carousel={carousel}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            schema({
              sectionHeadings,
              productDetails,
              tracking,
              retailers,
              currentUrl,
            })
          ),
        }}
      />
    </StandardLayout>
  );
};

const mapStateToProps = ({
  content,
  breakpoints,
  productSwatch,
  product,
  presentationLocale,
  status,
}) => {
  const selectedSKU = R.prop('selectedSKU')(productSwatch);

  const swatches = R.path(['productSwatch', 'swatches'])(content);
  const selectedSwatch = R.find(({ sku }) => sku === selectedSKU)(swatches);
  const retailersInfo = R.path(['retailersInfo'])(content);
  const retailerInfo = R.prop(selectedSKU)(retailersInfo);

  return {
    sectionHeadings: R.prop('sectionHeadings')(content),
    currentBreakpoint: R.prop('current')(breakpoints),
    productDetails: R.prop('productDetails')(content),
    retailerInfo,
    swatches,
    selectedSwatch,
    basketProduct: getBasketProduct({ content, selectedSwatch, retailerInfo }),
    breadcrumb: R.prop('breadcrumb')(content),
    isIngredientsOpen: R.prop('isIngredientsOpen')(product),
    subcategoryTitle: R.prop('subcategoryTitle')(content),
    categoryTitle: R.prop('subcategoryTitle')(content),
    tracking: R.prop('tracking')(content),
    retailers: R.prop('retailers')(content),
    presentationLocale,
    bvConfig: R.prop('bvConfig', content),
    customProductBuyLink: R.prop('customProductBuyLink', content),
    currentUrl: R.prop('currentUrl', status),
    hideNumberOfRetailers: R.prop('hideNumberOfRetailers', content),
    hidePrice: R.prop('hidePrice', content),
    skus: R.prop('skus', content),
    shadeFinder: R.prop('shadeFinder', content),
    enableShadeFinder: R.prop('enableShadeFinder', content),
    shadeFinderQuiz: R.prop('shadeFinderQuiz', content),
    productSlug: R.prop('productURL', content),
  };
};

const mapDispatchToProps = (dispatch) => ({
  toggle: () => dispatch(toggleAction()),
  fetchRetailers: ({ skus, locale }) =>
    dispatch(fetchRetailersAction({ skus, locale })),
});

Product.propTypes = {
  productDetails: PropTypes.shape({
    averageRating: PropTypes.any,
    benefits: PropTypes.arrayOf(PropTypes.any),
    buyButtonLabel: PropTypes.string,
    bvid: PropTypes.string,
    description: PropTypes.string,
    fromRetailersLabel: PropTypes.string,
    howToSteps: PropTypes.arrayOf(PropTypes.any),
    showIngredients: PropTypes.string,
    hideIngredients: PropTypes.string,
    longDescription: PropTypes.string,
    noReviewsLabel: PropTypes.string,
    numberOfReviews: PropTypes.number,
    outOfStockLabel: PropTypes.string,
    priceRetailersPluralLabel: PropTypes.string,
    priceRetailersSingularLabel: PropTypes.string,
    quantityLabel: PropTypes.string,
    readReviewsLabel: PropTypes.string,
    retailersNotFoundLabel: PropTypes.string,
    shadeSelectLabel: PropTypes.string,
    title: PropTypes.string,
  }),
  sectionHeadings: PropTypes.shape({
    relatedProducts: PropTypes.shape({
      heading: PropTypes.string,
    }),
    howToSteps: PropTypes.shape({
      heading: PropTypes.string,
    }),
    retailers: PropTypes.shape({
      heading: PropTypes.string,
    }),
  }),
  currentBreakpoint: PropTypes.string,
  swatches: PropTypes.arrayOf(
    PropTypes.shape({
      sku: PropTypes.string,
      itemName: PropTypes.string,
      skuColor: PropTypes.string,
    })
  ),
  selectedSwatch: PropTypes.shape({
    ingredients: PropTypes.string,
    itemName: PropTypes.string,
    sku: PropTypes.string,
    skuColor: PropTypes.string,
  }),
  retailerInfo: PropTypes.shape({
    numberOfRetailers: PropTypes.number,
    lowestPrice: PropTypes.shape({
      currencySymbol: PropTypes.string,
      number: PropTypes.number,
      price: PropTypes.string,
    }),
    inStock: PropTypes.bool,
  }),
  basketProduct: PropTypes.shape({
    name: PropTypes.string,
    shadeName: PropTypes.string,
    price: PropTypes.number,
    currency: PropTypes.string,
    image: PropTypes.string,
    ean: PropTypes.string,
    retailers: PropTypes.array,
    sku: PropTypes.string,
    productId: PropTypes.string,
  }),
  breadcrumb: PropTypes.arrayOf(PropTypes.object),
  isIngredientsOpen: PropTypes.bool,
  toggle: PropTypes.func,
  subcategoryTitle: PropTypes.string,
  categoryTitle: PropTypes.string,
  tracking: PropTypes.shape({
    currencyCode: PropTypes.string,
    product: PropTypes.arrayOf(
      PropTypes.shape({
        attributes: PropTypes.shape({
          productUrl: PropTypes.string,
        }),
      })
    ),
  }),
  retailers: PropTypes.arrayOf(
    PropTypes.shape({
      retailersList: PropTypes.arrayOf(
        PropTypes.shape({
          price: PropTypes.string,
          currencySymbol: PropTypes.string,
        })
      ),
    })
  ),
  presentationLocale: PropTypes.string.isRequired,
  bvConfig: PropTypes.objectOf(PropTypes.object).isRequired,
  customProductBuyLink: PropTypes.string,
  currentUrl: PropTypes.string.isRequired,
  hideNumberOfRetailers: PropTypes.bool,
  fetchRetailers: PropTypes.func,
  skus: PropTypes.string.isRequired,
  hidePrice: PropTypes.bool,
  shadeFinder: PropTypes.object,
  enableShadeFinder: PropTypes.bool,
  productSlug: PropTypes.string,
};

export default connect(mapStateToProps, mapDispatchToProps)(Product);
